import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import JsonLd from "@/components/JsonLd";
import { api, SITE_URL } from "@/lib/api";

function Stars({ rating, className = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} z 5 hviezdičiek`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${className} ${n <= rating ? "text-gold fill-gold" : "text-white/20"}`} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [data, setData] = useState({ items: [], average: 0, count: 0 });

  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data.count) return null; // žiadne reálne recenzie → sekcia sa nezobrazí

  const ld = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    "name": "Zateplenia.eu",
    "url": SITE_URL,
    "image": `${SITE_URL}/og-image.jpg`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": String(data.average),
      "reviewCount": String(data.count),
      "bestRating": "5",
      "worstRating": "1",
    },
    "review": data.items.map((r) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "datePublished": r.date,
      "reviewRating": { "@type": "Rating", "ratingValue": String(r.rating), "bestRating": "5" },
      "reviewBody": r.text,
    })),
  };

  return (
    <section id="recenzie" className="bg-ink py-20 md:py-28 border-t border-white/10" data-testid="reviews-section">
      <JsonLd id="ld-reviews" data={ld} />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Hodnotenia klientov</p>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] text-white max-w-2xl">
            Čo hovoria naši <span className="text-gold">zákazníci</span>
          </h2>
          <div className="flex items-center gap-4" data-testid="reviews-aggregate">
            <div className="font-display text-5xl text-gold leading-none">{data.average}</div>
            <div>
              <Stars rating={Math.round(data.average)} className="w-5 h-5" />
              <div className="text-white/50 text-sm mt-1">z {data.count} hodnotení</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {data.items.slice(0, 9).map((r) => (
            <div key={r.id} className="border border-white/10 bg-ink-2 p-6 flex flex-col" data-testid={`review-${r.id}`}>
              <Quote className="w-7 h-7 text-gold/40 mb-3" />
              <p className="text-white/75 text-sm leading-relaxed flex-1">{r.text}</p>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/8">
                <div>
                  <div className="text-white font-semibold text-sm">{r.name}</div>
                  {r.town && <div className="text-white/40 text-xs">{r.town}</div>}
                </div>
                <Stars rating={r.rating} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
